import { useMemo, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Plus, Search } from "lucide-react";
import { Button, Input } from "@/design-system/components/ui";
import { Select } from "@/components/ui/Select";
import { PageHeader } from "@/components/layout/PageHeader";
import { useToast } from "@/components/ui/Toast";
import { useAuth } from "@/hooks/useAuth";
import { adminWorkTaskTagsApi, type TaskTagAdminDto } from "@/api/workTasks";
import { ApiError } from "@/api/client";
import { TagListTable } from "./TagListTable";
import { TagEditorDialog } from "./TagEditorDialog";
import { TagMergeDialog } from "./TagMergeDialog";

type SortKey = "name" | "usage" | "modified";

const QUERY_KEY = ["admin", "work-tasks", "tags"] as const;

/**
 * Tasks v1.1 § 2 — admin landing for tag management. Top of page:
 * search box, "Show archived" toggle, sort, "New tag" button.
 * Body: <see cref="TagListTable"/>. Dialogs:
 * <see cref="TagEditorDialog"/> for create / edit and
 * <see cref="TagMergeDialog"/> for merge.
 */
export function TagManagementPage() {
  const { hasPermission } = useAuth();
  const canManage = hasPermission("Tasks.ManageTags");
  const toast = useToast();
  const qc = useQueryClient();

  const [search, setSearch] = useState("");
  const [includeArchived, setIncludeArchived] = useState(false);
  const [sort, setSort] = useState<SortKey>("name");

  const [editing, setEditing] = useState<TaskTagAdminDto | null | undefined>(undefined);
  const [merging, setMerging] = useState<TaskTagAdminDto | null>(null);

  const tagsQ = useQuery({
    queryKey: [...QUERY_KEY, includeArchived],
    queryFn: () => adminWorkTaskTagsApi.list(includeArchived),
    enabled: canManage,
  });

  const refresh = () => qc.invalidateQueries({ queryKey: QUERY_KEY });

  const visible = useMemo(() => {
    const rows = tagsQ.data ?? [];
    const needle = search.trim().toLowerCase();
    const filtered = needle.length === 0
      ? rows
      : rows.filter((r) => r.name.toLowerCase().includes(needle));
    const sorted = [...filtered];
    if (sort === "name") sorted.sort((a, b) => a.name.localeCompare(b.name));
    else if (sort === "usage") sorted.sort((a, b) => b.totalUsageCount - a.totalUsageCount);
    else sorted.sort((a, b) => b.modifiedDate.localeCompare(a.modifiedDate));
    return sorted;
  }, [tagsQ.data, search, sort]);

  const archive = async (t: TaskTagAdminDto) => {
    if (!window.confirm(
      `Archive '${t.name}'? It will stay attached to ${t.totalUsageCount} existing task` +
      `${t.totalUsageCount === 1 ? "" : "s"} but won't appear in the picker for new tasks.`,
    )) return;
    try {
      await adminWorkTaskTagsApi.archive(t.id);
      toast.success(`Tag '${t.name}' archived.`);
      refresh();
    } catch (err) {
      const msg = err instanceof ApiError ? err.errors?.[0] ?? err.message : "Could not archive.";
      toast.error(msg);
    }
  };

  const restore = async (t: TaskTagAdminDto) => {
    try {
      await adminWorkTaskTagsApi.restore(t.id);
      toast.success(`Tag '${t.name}' restored.`);
      refresh();
    } catch (err) {
      const msg = err instanceof ApiError ? err.errors?.[0] ?? err.message : "Could not restore.";
      toast.error(msg);
    }
  };

  if (!canManage) {
    return (
      <div className="text-small text-status-error">
        You do not have permission to manage tags.
      </div>
    );
  }

  return (
    <>
      <PageHeader
        eyebrow="Admin · Tasks"
        title="Tags"
        description="Create, rename, archive, or merge the tags admins can apply to tasks."
        actions={
          <Button type="button" onClick={() => setEditing(null)}>
            <Plus size={16} aria-hidden /> New tag
          </Button>
        }
      />

      <div className="flex flex-col md:flex-row md:items-end gap-space-3 mb-space-4">
        <div className="flex-1 min-w-0">
          <div className="relative">
            <Search
              size={16}
              aria-hidden
              className="absolute left-space-2 top-1/2 -translate-y-1/2 text-gray-mid"
            />
            <Input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search tags by name"
              className="pl-space-5"
              aria-label="Search tags"
            />
          </div>
        </div>
        <label className="flex items-center gap-space-2 text-small text-onyx">
          <input
            type="checkbox"
            checked={includeArchived}
            onChange={(e) => setIncludeArchived(e.target.checked)}
          />
          Show archived
        </label>
        <Select
          value={sort}
          onChange={(e) => setSort(e.target.value as SortKey)}
          aria-label="Sort tags"
        >
          <option value="name">Name (A–Z)</option>
          <option value="usage">Usage (high → low)</option>
          <option value="modified">Last modified</option>
        </Select>
      </div>

      <TagListTable
        rows={visible}
        loading={tagsQ.isLoading}
        error={tagsQ.isError ? (tagsQ.error as Error).message : null}
        onEdit={(t) => setEditing(t)}
        onMerge={(t) => setMerging(t)}
        onArchive={archive}
        onRestore={restore}
      />

      <TagEditorDialog
        open={editing !== undefined}
        tag={editing ?? null}
        onClose={() => setEditing(undefined)}
        onSaved={refresh}
      />

      <TagMergeDialog
        open={Boolean(merging)}
        source={merging}
        candidates={tagsQ.data ?? []}
        onClose={() => setMerging(null)}
        onMerged={refresh}
      />
    </>
  );
}
